import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { isNativeApp } from "@/lib/platform";
import { configureRevenueCat } from "@/lib/revenuecat";
import { getAppleIdToken, getEmergentSessionId, getGoogleIdToken, isCancelError, signOutSocial } from "@/lib/social-auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tara_session_token";
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Global axios interceptor — attach Bearer token to ALL requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// No response at all means the request never reached our API (offline, wrong
// REACT_APP_BACKEND_URL, server down), which deserves a different message.
function apiErrorMessage(e) {
  console.warn("API request failed", e.config?.method, e.config?.url, e.response?.status, e.message);
  if (!e.response) {
    const host = (process.env.REACT_APP_BACKEND_URL || "").replace(/^https?:\/\//, "");
    return `Can't reach the Tara Kundli server${host ? ` (${host})` : ""}. Check your connection and try again.`;
  }
  const detail = e.response.data?.detail;
  if (detail == null) return `Server error (${e.response.status}). Please try again.`;
  return formatApiErrorDetail(detail);
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Identify the user to RevenueCat on native apps (no-op on web).
  useEffect(() => {
    if (user?.user_id) configureRevenueCat(user.user_id);
  }, [user?.user_id]);

  // Google web redirect flow — disabled inside the native app (mobile uses email/password)
  const login = () => {
    if (isNativeApp()) return;
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data?.token) localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      return { success: true, user: res.data.user };
    } catch (e) {
      return { success: false, error: apiErrorMessage(e) };
    }
  };

  const registerWithPassword = async (email, password, name) => {
    try {
      const res = await axios.post(`${API}/auth/register`, { email, password, name });
      if (res.data?.token) localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      return { success: true, user: res.data.user };
    } catch (e) {
      return { success: false, error: apiErrorMessage(e) };
    }
  };

  // Native (iOS/Android) Google / Apple sign-in: OS sign-in sheet -> ID token -> our session.
  const loginWithProvider = async (label, getToken, endpoint) => {
    let payload;
    try {
      payload = await getToken();
    } catch (e) {
      if (isCancelError(e)) return { success: false, cancelled: true };
      console.warn(`${label} sign-in failed`, e?.code, e?.message);
      return { success: false, error: `Couldn't complete ${label} sign-in${e?.code ? ` (${e.code})` : ""}. Please try again.` };
    }
    try {
      const res = await axios.post(`${API}${endpoint}`, payload);
      if (res.data?.token) localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      return { success: true, user: res.data.user };
    } catch (e) {
      return { success: false, error: apiErrorMessage(e) };
    }
  };

  const loginWithGoogleNative = () => loginWithProvider(
    "Google", async () => ({ id_token: await getGoogleIdToken() }), "/auth/google");

  const loginWithGoogleEmergent = () => loginWithProvider(
    "Google", async () => ({ session_id: await getEmergentSessionId() }), "/auth/session");

  const loginWithAppleNative = () => loginWithProvider(
    "Apple", async () => {
      const { idToken, name } = await getAppleIdToken();
      return { id_token: idToken, name };
    }, "/auth/apple");

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    signOutSocial();
  };

  // Permanently deletes the account on the server, then signs out locally.
  const deleteAccount = async () => {
    try {
      await axios.delete(`${API}/auth/account`);
    } catch (e) {
      return { success: false, error: apiErrorMessage(e) };
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    signOutSocial();
    return { success: true };
  };

  const setAuthData = (userData, token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    setUser(userData);
  };

  const setUserData = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, loginWithPassword, registerWithPassword, loginWithGoogleNative, loginWithGoogleEmergent, loginWithAppleNative, logout, deleteAccount,
      setUserData, setAuthData,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
